/**
 * Echo Hunt -- hunt scoring (P9, §12 + §10.1 + §9.2).
 *
 * Everything here is PURE and label-aware on purpose: this module is the one place the truth is used,
 * and the UI only calls it AFTER the reveal (`Round_Live.SubmitRound_Live`). The pre-reveal components
 * receive `HuntRoundView` from `Round.ts`, which carries no per-slot label at all (§16.0).
 *
 * ## The three numbers (§12)
 *
 *  1. **Precision** -- "what percentage of your marks were correct" (§10.1). The headline number the
 *     user sees, and the one that is easy to game by marking fewer bullets, which is why it is never
 *     shown alone.
 *  2. **Chance-corrected precision** -- `(p - d/N) / (1 - d/N)`, where `d/N` is the share of the story
 *     that came from the journal (§12). Random marking on a story with a 65% real share already scores
 *     65%, so raw precision would hand out a free 0.65 to a coin-flipper. This is the number that can
 *     be compared between rounds with different X.
 *  3. **AUC** -- `P(a randomly chosen real bullet is rated above a randomly chosen fake)` (§9.2). It
 *     uses every confidence instead of only the top X, so it does not move when X does.
 *
 * ## The `2X(1-X)` ceiling (§9.2)
 *
 * `RandomJudgeMiscategorizationPct` is the theoretical ceiling of the §9.1 "top-X bucket" metric for a
 * judge with NO information: it marks X of N as dream-sourced at random and misclassifies
 * `2X(1-X)` of them. At X = .5 that is 50%, at X = .3 it is 42% -- which is the whole reason §9.2 wants
 * a *band* rather than "as close to chance as possible", and why a confidently WRONG judge is also a
 * tell. P10's judge loop is what consumes it; it lives here because it is a property of the metric, and
 * P9's acceptance criterion asks for it to be pinned now.
 *
 * PURE module: no React, no `store`, no DB imports (see README.md).
 *
 * See `Docs/Plans/Others/2026-10-19_EchoHunt.md` §10.1, §10.3, §12, §9.2, §16.0.
 */
import {Answer, RoundSlot, SlotSourceKind} from "./Types.js";

// confidence + marking (§10.1)
// ==========

/** Neutral confidence: "I have no idea", and the default for an untouched bullet. 50 rather than 0 so
 * that an unanswered bullet is not silently voted "fake" -- that would make a half-played round score
 * as a confident (and wrong) guess instead of as a non-answer. */
export const DEFAULT_CONFIDENCE = 50;

export function ClampConfidence(value: number): number {
	if (!Number.isFinite(value)) return DEFAULT_CONFIDENCE;
	return Math.max(0, Math.min(100, Math.round(value)));
}

/** The minimal answer shape the marker/scorer needs; `Answer` is structurally assignable. */
export type HuntAnswerConfidence = {
	slotId: string;
	confidence: number;
};

/** The minimal slot shape the scorer needs; `RoundSlot` is structurally assignable. */
export type HuntSlotLabels = {
	slotId: string;
	sourceKind: SlotSourceKind;
};

/** Which slots the user's guess-list contains (§10.1).
 *
 * The plan does not ask the user to tick bullets: it tells them how many are dream-sourced ("X") and
 * highlights the X entries they have rated most dream-sourced. So `marked` is DERIVED from the
 * confidences, and this function is the single definition of that derivation -- the highlight in the UI
 * and the scoring both call it, so they can never disagree about what the user guessed.
 *
 * Ties are broken by STORY ORDER (`slotOrder`), not by id: an equal-confidence run should read as the
 * bullets the user saw first, and a random tiebreak would make the same answer sheet score differently
 * on two runs.
 */
export function SelectMarkedSlotIds(answers: HuntAnswerConfidence[]|n, realCount: number, slotOrder?: string[]|n): string[] {
	const answerList = answers ?? [];
	const confidenceBySlot = new Map(answerList.map(answer=>[answer.slotId, ClampConfidence(answer.confidence)]));
	// story order when the caller knows it, otherwise the answers' own order (which the UI keeps in sync
	// with the story) -- never a hash order
	const order = slotOrder != null ? [...slotOrder] : answerList.map(answer=>answer.slotId);

	const wanted = Math.max(0, Math.min(Math.round(realCount), order.length));
	const ranked = order
		.map((slotId, index)=>({slotId, index, confidence: confidenceBySlot.get(slotId) ?? DEFAULT_CONFIDENCE}))
		.sort((a, b)=>b.confidence - a.confidence || a.index - b.index);

	return ranked.slice(0, wanted).map(a=>a.slotId);
}

/** One answer sheet with every slot at neutral confidence. The UI starts from this so an untouched
 * bullet is a genuine non-answer rather than an absent key. */
export function CreateNeutralAnswers(slots: {slotId: string}[]|n): HuntAnswerConfidence[] {
	return (slots ?? []).map(slot=>({slotId: slot.slotId, confidence: DEFAULT_CONFIDENCE}));
}

// per-slot outcome (§10.1 reveal)
// ==========

export type HuntOutcome = "hit" | "miss" | "falseAlarm" | "correctReject";

export type ScoredSlot = {
	slotId: string;
	isReal: boolean;
	confidence: number;
	marked: boolean;
	/** `hit` = marked and real, `falseAlarm` = marked and fake, `miss` = unmarked and real,
	 * `correctReject` = unmarked and fake. */
	outcome: HuntOutcome;
};

export type HuntScore = {
	totalSlots: number;
	/** how many bullets really came from the journal -- the "X" the user was told (§10.1) */
	realCount: number;
	markedCount: number;

	hits: number;
	misses: number;
	falseAlarms: number;
	correctRejects: number;

	/** correct marks / marks; 0 when nothing was marked (§10.1 "score") */
	precision: number;
	/** realCount / totalSlots -- what random marking would score (§12) */
	chancePrecision: number;
	/** `(precision - chancePrecision) / (1 - chancePrecision)` (§12) */
	chanceCorrected: number;
	/** correct marks / realCount -- how much of the dream content was actually found */
	recall: number;
	/** P(random real rated above random fake), ties counted as .5; .5 when a side is empty (§9.2) */
	auc: number;

	perSlot: ScoredSlot[];
};

/** Scores one finished round (§12). `options.markedSlotIds` lets the caller pass marks it already
 * derived (e.g. the exact set that was highlighted while playing), so the reveal cannot disagree with
 * what the user saw; omitting it re-derives them from the confidences. */
export function ScoreHunt(answers: HuntAnswerConfidence[]|n, slots: HuntSlotLabels[]|n, options?: {markedSlotIds?: string[]|n}): HuntScore {
	const slotList = slots ?? [];
	const answerList = answers ?? [];
	const confidenceBySlot = new Map(answerList.map(answer=>[answer.slotId, ClampConfidence(answer.confidence)]));

	const realCount = slotList.filter(slot=>slot.sourceKind == "real").length;
	const markedIds = options?.markedSlotIds != null
		? new Set(options.markedSlotIds)
		: new Set(SelectMarkedSlotIds(answerList, realCount, slotList.map(slot=>slot.slotId)));

	const perSlot: ScoredSlot[] = slotList.map(slot=>{
		const isReal = slot.sourceKind == "real";
		const marked = markedIds.has(slot.slotId);
		const outcome: HuntOutcome = marked
			? (isReal ? "hit" : "falseAlarm")
			: (isReal ? "miss" : "correctReject");
		return {
			slotId: slot.slotId,
			isReal,
			confidence: confidenceBySlot.get(slot.slotId) ?? DEFAULT_CONFIDENCE,
			marked,
			outcome,
		};
	});

	const hits = perSlot.filter(a=>a.outcome == "hit").length;
	const misses = perSlot.filter(a=>a.outcome == "miss").length;
	const falseAlarms = perSlot.filter(a=>a.outcome == "falseAlarm").length;
	const correctRejects = perSlot.filter(a=>a.outcome == "correctReject").length;
	const markedCount = hits + falseAlarms;

	const precision = markedCount == 0 ? 0 : hits / markedCount;
	const chancePrecision = slotList.length == 0 ? 0 : realCount / slotList.length;
	// with every bullet real there is nothing to discriminate, so there is no "improvement over chance"
	// to measure -- 0 rather than a division by zero (or a misleading 1)
	const chanceCorrected = chancePrecision >= 1 - 1e-9
		? 0
		: (precision - chancePrecision) / (1 - chancePrecision);

	return {
		totalSlots: slotList.length,
		realCount,
		markedCount,
		hits,
		misses,
		falseAlarms,
		correctRejects,
		precision,
		chancePrecision,
		chanceCorrected,
		recall: realCount == 0 ? 0 : hits / realCount,
		auc: ComputeAUC(perSlot.map(a=>({confidence: a.confidence, isReal: a.isReal}))),
		perSlot,
	};
}

// AUC (§9.2)
// ==========

/** `P(a real bullet's confidence > a fake bullet's confidence)`, ties counted as .5 (§9.2).
 *
 * Deliberately the pairwise definition rather than the rank-sum shortcut: at journal scale a round has
 * ~10 bullets, the pairwise form has no tie-handling pitfalls, and it is the definition the plan
 * actually states, so a reader can check the code against §9.2 directly.
 *
 * Returns .5 when either side is empty: with no reals (or no fakes) there is no discrimination to
 * measure, and 0 or 1 would both read as a confident result. */
export function ComputeAUC(items: {confidence: number, isReal: boolean}[]|n): number {
	const list = items ?? [];
	const reals = list.filter(a=>a.isReal).map(a=>ClampConfidence(a.confidence));
	const fakes = list.filter(a=>!a.isReal).map(a=>ClampConfidence(a.confidence));
	if (reals.length == 0 || fakes.length == 0) return .5;

	let numerator = 0;
	for (const real of reals) {
		for (const fake of fakes) {
			if (real > fake) numerator += 1;
			else if (real == fake) numerator += .5;
		}
	}
	return numerator / (reals.length * fakes.length);
}

// the chance ceiling of the §9.1 metric (§9.2)
// ==========

/** The miscategorization % an INFORMATION-FREE judge reaches on the §9.1 top-X-bucket metric, in
 * percent (§9.2): `2X(1-X) * 100` -- 50% at X = .5, 42% at X = .3.
 *
 * This is the ceiling the judge loop's Y target must be compared against. A judge scoring well ABOVE
 * this is genuinely seeing something (so the round is too easy), and one scoring well BELOW it is
 * systematically inverted (also a tell), which is why §9.2 asks for a two-sided band.
 *
 * P10 owns the loop; this lives here because it is a property of the metric, and because a Y target of
 * .5 is only meaningful next to it (Y = .5 is unreachable-favourable at X = .3, where chance alone
 * yields 42%). */
export function RandomJudgeMiscategorizationPct(xShare: number): number {
	const x = Math.max(0, Math.min(1, Number.isFinite(xShare) ? xShare : 0));
	return 2 * x * (1 - x) * 100;
}

// answer-sheet helpers (§10.3, §13.1)
// ==========

/** The answers a finished round should be persisted as: the drafts the UI collected, in STORY order,
 * with only the fields the plan actually asks for (§13.1).
 *
 * Story order rather than submission order because the reveal and the §13.3 diagnostics both read the
 * sheet alongside the story, and a reordered sheet is much harder to check by eye.
 */
export function ToAnswerList(slotOrder: string[]|n, drafts: {
	slotId: string;
	confidence?: number|n;
	recallKind?: Answer["recallKind"];
	why?: string|n;
	msToMark?: number|n;
	llmTellFlag?: boolean|n;
	tellPhrase?: string|n;
}[]|n): Answer[] {
	const draftBySlot = new Map((drafts ?? []).map(draft=>[draft.slotId, draft]));
	const order = slotOrder != null ? [...slotOrder] : (drafts ?? []).map(a=>a.slotId);

	const answers: Answer[] = [];
	for (const slotId of order) {
		const draft = draftBySlot.get(slotId);
		if (draft == null) continue;
		answers.push({
			slotId,
			// `marked` is stored for the log's sake only; the authoritative derivation is
			// `SelectMarkedSlotIds` and a reader must not treat the stored flag as the source of truth
			marked: false,
			confidence: ClampConfidence(draft.confidence ?? DEFAULT_CONFIDENCE),
			recallKind: draft.recallKind ?? null,
			why: TrimToNull(draft.why),
			msToMark: Math.max(0, Math.round(draft.msToMark ?? 0)),
			llmTellFlag: draft.llmTellFlag ?? null,
			tellPhrase: TrimToNull(draft.tellPhrase),
		});
	}
	return answers;
}

/** Fills each answer's stored `marked` flag from the derived guess-list, so the round log is
 * self-describing without a reader having to re-derive it. Pure: returns new objects. */
export function ApplyMarkedFlags(answers: Answer[]|n, markedSlotIds: string[]|n): Answer[] {
	const marked = new Set(markedSlotIds ?? []);
	return (answers ?? []).map(answer=>({...answer, marked: marked.has(answer.slotId)}));
}

/** The answer sheet as the marker/highlighter wants it. */
export function ToConfidenceList(answers: Answer[]|n): HuntAnswerConfidence[] {
	return (answers ?? []).map(answer=>({slotId: answer.slotId, confidence: ClampConfidence(answer.confidence)}));
}

/** Slots the reveal should offer the tell box for (§10.3): the fakes the user ended up calling fake,
 * i.e. the ones that fell OUTSIDE their guessed dream set.
 *
 * Deliberately keyed on the scored OUTCOME (`correctReject`) rather than on a confidence cutoff, because
 * the slider is a relative ranking rather than an absolute scale. A fake left out of a 7-of-10 guess list
 * at confidence 60 was still called fake by the player -- the same verdict as one left out at 25 -- so a
 * "<= 50" rule would silently withhold the box from a tell the player actually has, which is exactly the
 * bug this replaces. Use `a.marked` for "they called it a dream" and this for "they called it fake".
 *
 * A fake the user MARKED (a false alarm) is not a tell they can name -- they already said they thought it
 * was theirs -- and a real bullet the user called fake is the more interesting case, which the §13.3
 * "camouflaged dream content" list covers instead. */
export function SelectTellCandidateSlotIds(score: HuntScore, slots: HuntSlotLabels[]|n): string[] {
	const slotsById = new Map((slots ?? []).map(slot=>[slot.slotId, slot]));
	return score.perSlot
		.filter(a=>{
			const slot = slotsById.get(a.slotId);
			return slot != null && slot.sourceKind == "fake" && a.outcome == "correctReject";
		})
		.map(a=>a.slotId);
}

/** The real slots of a round (the ones the reveal shows a raw text + date for, §10.4). */
export function SelectRealSlotIds(slots: HuntSlotLabels[]|n): string[] {
	return (slots ?? []).filter(slot=>slot.sourceKind == "real").map(slot=>slot.slotId);
}

function TrimToNull(value: string|n): string|null {
	const trimmed = value?.trim();
	return trimmed != null && trimmed.length > 0 ? trimmed : null;
}

/** Applies the answers' confidence to a round's slots, without touching anything else. Exported because
 * the reveal's per-slot display needs the same pairing the scorer used. */
export function PairAnswersWithSlots(answers: Answer[]|n, slots: RoundSlot[]|n): {slot: RoundSlot, answer: Answer|n}[] {
	const answerBySlot = new Map((answers ?? []).map(answer=>[answer.slotId, answer]));
	return (slots ?? []).map(slot=>({slot, answer: answerBySlot.get(slot.slotId) ?? null}));
}
